import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowUpDown,
  ExternalLink,
  FileText,
  Filter,
  MapPin,
  Pencil,
  Search,
  Send,
  Star,
  UserCheck,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * ApplicantKanban — a horizontally-scrollable board with one column per
 * pipeline stage. Cards can be dragged between columns to update
 * `applicant.stage` on the fly.
 *
 * All heavy lifting (patching, geocoding, etc.) is delegated to the parent
 * via `onPatch` and `onInvite` — this component is *purely* a presentational
 * board that owns nothing except its local filters and drag state.
 *
 * Uses native HTML5 drag-and-drop so we don't take a new dependency.
 */

// Stages in the order they appear across the board — mirrors the pipeline
// flow so left-to-right = "cold → contract → onboarded".
const STAGE_ORDER = [
  "Not Contacted",
  "Contacted",
  "In Review",
  "Interview Scheduled",
  "Accepted",
  "Rejected",
];

// Per-stage visual identity — accent color for the column header + card
// left border. Kept in sync with the pipeline row's stage pill palette.
const STAGE_ACCENT = {
  "Not Contacted":       "#78584B",
  "Contacted":           "#B08D57",
  "In Review":           "#BF360C",
  "Interview Scheduled": "#0D47A1",
  "Accepted":            "#1B5E20",
  "Rejected":            "#4D3F35",
};

// One-line description of what each stage means — surfaced in the column
// header tooltip and, for empty columns, as ghost helper text.
const STAGE_HINT = {
  "Not Contacted":       "Fresh leads awaiting first outreach.",
  "Contacted":           "First message sent — awaiting reply.",
  "In Review":           "Portfolio + fit review in progress.",
  "Interview Scheduled": "Call on the calendar.",
  "Accepted":            "Cleared the interview — ready to onboard.",
  "Rejected":            "Not a fit — kept for records.",
};

// Per-column sorters — each column owns its own sort so a crowded lane can
// be prioritized independently. `recency` is the default so freshly-arrived
// applicants float to the top of every column, matching the pipeline view.
const COLUMN_SORTERS = {
  recency: {
    label: "RECENT",
    cmp: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
  },
  score: {
    label: "SCORE",
    cmp: (a, b) =>
      (b.score || 0) - (a.score || 0)
      || (b.created_at || "").localeCompare(a.created_at || ""),
  },
  name: {
    label: "NAME",
    cmp: (a, b) => (a.name || "").localeCompare(b.name || ""),
  },
};
const SORT_KEYS = Object.keys(COLUMN_SORTERS);

export default function ApplicantKanban({ applicants, regions, onPatch, onInvite, onEdit }) {
  const [query, setQuery] = useState("");
  const [region, setRegion] = useState("All");
  const [minScore, setMinScore] = useState(0);
  const [dragOverStage, setDragOverStage] = useState(null);
  // Per-column sort key — { [stage]: "recency" | "score" | "name" }. Only
  // populated when the admin explicitly picks a non-default so most columns
  // stay untouched. `getColumnSort` falls back to "recency".
  const [sortByColumn, setSortByColumn] = useState({});
  const getColumnSort = (stage) => sortByColumn[stage] || "recency";
  const setColumnSort = (stage, key) =>
    setSortByColumn((cur) => ({ ...cur, [stage]: key }));

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return applicants.filter((a) => {
      if (region !== "All" && a.region !== region) return false;
      if ((a.score || 0) < minScore) return false;
      if (!q) return true;
      const hay =
        (a.name || "") +
        " " +
        (a.email || "") +
        " " +
        (a.zip_code || "") +
        " " +
        (a.desired_location || "") +
        " " +
        (a.resolved_city || "");
      return hay.toLowerCase().includes(q);
    });
  }, [applicants, query, region, minScore]);

  const byStage = useMemo(() => {
    const map = Object.fromEntries(STAGE_ORDER.map((s) => [s, []]));
    for (const a of filtered) {
      (map[a.stage] || (map[a.stage] = [])).push(a);
    }
    // Apply the per-column sort so a crowded lane can be prioritised without
    // affecting the neighbours. Fresh columns stay on the default recency.
    for (const stage of STAGE_ORDER) {
      const key = sortByColumn[stage] || "recency";
      const sorter = COLUMN_SORTERS[key];
      if (sorter) map[stage].sort(sorter.cmp);
    }
    return map;
  }, [filtered, sortByColumn]);

  // Drag events use dataTransfer so browsers don't complain, but we key off
  // an internal ref-less object so we never depend on the dropped payload.
  const [dragging, setDragging] = useState(null); // { id, fromStage }
  const onDragStart = (applicant) => (e) => {
    setDragging({ id: applicant.id, fromStage: applicant.stage });
    // Firefox needs *something* set on dataTransfer to start the drag.
    try { e.dataTransfer.setData("text/plain", applicant.id); } catch (_) {}
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragEnd = () => {
    setDragging(null);
    setDragOverStage(null);
  };
  const onDragOver = (stage) => (e) => {
    if (!dragging) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverStage !== stage) setDragOverStage(stage);
  };
  const onDrop = (stage) => async (e) => {
    e.preventDefault();
    if (!dragging || dragging.fromStage === stage) {
      onDragEnd();
      return;
    }
    const applicant = applicants.find((a) => a.id === dragging.id);
    onDragEnd();
    if (!applicant) return;
    try {
      await onPatch(applicant, { stage });
      toast.success(`${applicant.name} → ${stage}`);
    } catch (_) {
      // parent already surfaces the error toast
    }
  };

  const activeFilterCount =
    (query ? 1 : 0) + (region !== "All" ? 1 : 0) + (minScore > 0 ? 1 : 0);

  return (
    <div className="space-y-4" data-testid="applicants-kanban">
      {/* FILTER BAR */}
      <div className="rounded-sm border border-[#1A0F0A]/12 bg-[#FBF4E4] px-3 py-2.5 flex flex-wrap items-center gap-3" data-testid="kanban-filters">
        <div className="inline-flex items-center gap-1.5 font-mono-tag text-[10px] text-[#78584B] tracking-wider">
          <Filter className="w-3 h-3" strokeWidth={2} /> FILTER
        </div>

        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-3.5 h-3.5 text-[#78584B] absolute left-2.5 top-1/2 -translate-y-1/2" strokeWidth={1.75} />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, city, ZIP…"
            data-testid="kanban-search"
            className="pl-8 h-8 bg-white/60 border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-xs text-[#1A0F0A]"
          />
        </div>

        <Select value={region} onValueChange={setRegion}>
          <SelectTrigger
            data-testid="kanban-region-filter"
            className="h-8 min-w-[170px] bg-white/60 border-[#1A0F0A]/15 rounded-sm text-xs text-[#1A0F0A] font-bold tracking-wide"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="All" data-testid="kanban-region-All">All regions</SelectItem>
            {(regions || []).map((r) => (
              <SelectItem key={r} value={r} data-testid={`kanban-region-${r.replace(/ /g, "-")}`}>
                {r}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="inline-flex items-center gap-1">
          <span className="font-mono-tag text-[10px] text-[#78584B] mr-1 tracking-wider">MIN SCORE</span>
          {[0, 1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setMinScore(n)}
              data-testid={`kanban-min-score-${n}`}
              className={`w-7 h-7 rounded-sm border text-[10px] font-bold transition-colors ${
                minScore === n
                  ? "bg-[#6B0F1A] text-[#F5EBD9] border-[#6B0F1A]"
                  : "bg-transparent text-[#78584B] border-[#1A0F0A]/15 hover:bg-[#1A0F0A]/5"
              }`}
            >
              {n === 0 ? "0" : `${n}★`}
            </button>
          ))}
        </div>

        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={() => { setQuery(""); setRegion("All"); setMinScore(0); }}
            data-testid="kanban-clear-filters"
            className="ml-auto inline-flex items-center gap-1 text-[10px] font-mono-tag text-[#6B0F1A] hover:underline"
          >
            <X className="w-3 h-3" strokeWidth={2} />
            CLEAR {activeFilterCount} FILTER{activeFilterCount === 1 ? "" : "S"}
          </button>
        )}

        <div className="font-mono-tag text-[10px] text-[#78584B] tracking-wider">
          {filtered.length} SHOWN · {applicants.length} TOTAL
        </div>
      </div>

      {/* BOARD — responsive grid: 1 column on phones, 2 on tablets, 3 on
          laptops, all 6 on ultra-wide monitors. Kills the horizontal scroll
          nuisance while keeping the left→right pipeline reading order intact
          within each row. */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3"
        data-testid="kanban-board"
      >
        {STAGE_ORDER.map((stage) => {
          const items = byStage[stage] || [];
          const accent = STAGE_ACCENT[stage];
          const isDropTarget = dragOverStage === stage && dragging?.fromStage !== stage;
          return (
            <div
              key={stage}
              data-testid={`kanban-column-${stage.replace(/ /g, "-").toLowerCase()}`}
              onDragOver={onDragOver(stage)}
              onDragLeave={() => setDragOverStage((s) => (s === stage ? null : s))}
              onDrop={onDrop(stage)}
              className={`rounded-sm border transition-all min-w-0 ${
                isDropTarget
                  ? "border-[#6B0F1A] bg-[#F5EBD9] shadow-[0_0_0_2px_#6B0F1A22]"
                  : "border-[#1A0F0A]/12 bg-[#FBF4E4]"
              }`}
            >
                {/* Column header */}
                <div
                  className="px-3 py-2.5 flex items-center justify-between gap-2 border-b border-[#1A0F0A]/10"
                  style={{ boxShadow: `inset 3px 0 0 ${accent}` }}
                  title={STAGE_HINT[stage]}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ background: accent }}
                    />
                    <span className="font-display text-sm font-bold text-[#1A0F0A] truncate tracking-tight">
                      {stage}
                    </span>
                  </div>
                  <span
                    className="font-mono-tag text-[10px] px-1.5 py-0.5 rounded-sm shrink-0"
                    style={{
                      background: `${accent}18`,
                      color: accent,
                    }}
                  >
                    {items.length}
                  </span>
                </div>

                {/* Cards */}
                <div className="p-2 space-y-2 min-h-[120px] max-h-[65vh] overflow-y-auto">
                  <AnimatePresence initial={false}>
                    {items.map((a) => (
                      <KanbanCard
                        key={a.id}
                        applicant={a}
                        accent={accent}
                        dragging={dragging?.id === a.id}
                        onDragStart={onDragStart(a)}
                        onDragEnd={onDragEnd}
                        onInvite={onInvite}
                        onEdit={onEdit}
                      />
                    ))}
                  </AnimatePresence>
                  {items.length === 0 && (
                    <div
                      className={`rounded-sm border border-dashed p-4 text-center transition-colors ${
                        isDropTarget
                          ? "border-[#6B0F1A]/50 bg-[#6B0F1A]/5"
                          : "border-[#1A0F0A]/15"
                      }`}
                    >
                      <p className="text-[11px] text-[#78584B]/80 italic leading-relaxed">
                        {isDropTarget ? "Drop here to move" : STAGE_HINT[stage]}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}

/* -------------------------- Column sort menu --------------------------- */
/**
 * Compact click-to-cycle sort control that lives inside each column header.
 * Cycles Recency → Score → Name → Recency. Kept as a button (not a Select)
 * so the header stays tight and the interaction is one tap on mobile.
 *
 * The current sort key is shown as a tiny label next to the icon so admins
 * always see what they're looking at without opening any menu.
 */
function ColumnSortMenu({ stage, value, onChange, accent }) {
  const nextKey = () => {
    const idx = SORT_KEYS.indexOf(value);
    return SORT_KEYS[(idx + 1) % SORT_KEYS.length];
  };
  const current = COLUMN_SORTERS[value];
  const testStage = stage.replace(/ /g, "-").toLowerCase();
  return (
    <button
      type="button"
      onClick={() => onChange(nextKey())}
      data-testid={`kanban-sort-${testStage}`}
      data-sort-key={value}
      title={`Sort by ${current.label.toLowerCase()} — click to cycle`}
      className="inline-flex items-center gap-1 h-6 px-1.5 rounded-sm border border-[#1A0F0A]/10 bg-white/40 hover:bg-white/80 transition-colors"
      style={{ color: accent }}
    >
      <ArrowUpDown className="w-2.5 h-2.5" strokeWidth={2.25} />
      <span className="font-mono-tag text-[9px] tracking-wider font-bold">
        {current.label}
      </span>
    </button>
  );
}

/* -------------------------- Card ---------------------------------------- */
function KanbanCard({ applicant, accent, dragging, onDragStart, onDragEnd, onInvite, onEdit }) {  const stars = applicant.score || 0;
  const city =
    applicant.resolved_city ||
    applicant.desired_location ||
    applicant.region ||
    "—";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: dragging ? 0.4 : 1, y: 0 }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.18 }}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      data-testid={`kanban-card-${applicant.id}`}
      className={`group rounded-sm bg-white border cursor-grab active:cursor-grabbing hover:shadow-[0_6px_16px_-6px_rgba(26,15,10,0.25)] transition-shadow ${
        dragging ? "border-[#6B0F1A]/40" : "border-[#1A0F0A]/10"
      }`}
      style={{ boxShadow: `inset 2px 0 0 ${accent}` }}
    >
      <button
        type="button"
        onClick={() => onEdit(applicant)}
        className="block w-full text-left p-3"
        data-testid={`kanban-card-open-${applicant.id}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="font-display text-sm font-bold text-[#1A0F0A] truncate">
              {applicant.name}
            </div>
            <div className="mt-0.5 flex items-center gap-1 text-[11px] text-[#4D3F35] min-w-0">
              <MapPin className="w-3 h-3 text-[#78584B] shrink-0" strokeWidth={2} />
              <span className="truncate">{city}</span>
              {applicant.zip_code && (
                <span className="font-mono-tag text-[9px] text-[#78584B] ml-1 shrink-0">
                  · {applicant.zip_code}
                </span>
              )}
            </div>
          </div>
          {stars > 0 && (
            <div className="inline-flex items-center gap-0.5 shrink-0" title={`Score ${stars}/5`}>
              <Star className="w-3 h-3 fill-[#B08D57] text-[#B08D57]" strokeWidth={1.75} />
              <span className="font-mono-tag text-[10px] font-bold text-[#B08D57]">{stars}</span>
            </div>
          )}
        </div>

        {applicant.notes && (
          <p className="mt-2 text-[11px] text-[#78584B] leading-snug line-clamp-2">
            {applicant.notes}
          </p>
        )}
      </button>

      {/* Card footer — quick actions. Kept outside the main <button> so
          nested clicks don't bubble into the edit sheet. */}
      <div className="px-3 pb-2.5 pt-1 flex items-center gap-1.5 flex-wrap border-t border-[#1A0F0A]/6">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onInvite(applicant); }}
          disabled={!applicant.email}
          data-testid={`kanban-onboard-${applicant.id}`}
          title={
            applicant.email
              ? "Start onboarding — copies this applicant to the roster and sends the magic link"
              : "Needs an email first"
          }
          className={`inline-flex items-center gap-1 h-6 px-2 rounded-sm text-[10px] font-bold tracking-wide transition-colors ${
            applicant.email
              ? "bg-[#6B0F1A] text-[#F5EBD9] hover:bg-[#8A1424]"
              : "bg-transparent text-[#78584B]/60 border border-[#1A0F0A]/10 cursor-not-allowed"
          }`}
        >
          <Send className="w-2.5 h-2.5" strokeWidth={2.25} /> ONBOARD
        </button>
        {applicant.resume_url && (
          <a
            href={applicant.resume_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            data-testid={`kanban-resume-${applicant.id}`}
            title={`Open resume · ${applicant.resume_url}`}
            className="inline-flex items-center gap-1 h-6 px-2 rounded-sm border border-[#6B0F1A]/30 text-[10px] font-bold tracking-wide text-[#6B0F1A] hover:bg-[#6B0F1A]/8"
          >
            <FileText className="w-2.5 h-2.5" strokeWidth={2.25} />
            RESUME
            <ExternalLink className="w-2 h-2 opacity-70" strokeWidth={2.25} />
          </a>
        )}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onEdit(applicant); }}
          data-testid={`kanban-edit-${applicant.id}`}
          className="ml-auto p-1 rounded-sm text-[#78584B] hover:text-[#6B0F1A] hover:bg-[#6B0F1A]/8"
          title="Edit"
        >
          <Pencil className="w-3 h-3" strokeWidth={2} />
        </button>
      </div>
    </motion.div>
  );
}
